import { BeneficiaryDesignationTier } from '@/types/schema';
import { formatCurrency } from '@/utils/formatting/currency';

import {
  ApplicationRecordChanges,
  entityKindLabel,
  formatOptionalCurrency,
  insurancePolicyKindLabel,
  organizationKindLabel,
  provisionContextLabel,
  recipientLabel,
  referenceLabel,
  referenceListLabel,
  relationshipLabel,
  shareLabel,
  testamentaryEntityKindLabel,
  transferLabel,
} from './ApplicationRecordChangesConfirmationContent.utils';
import {
  ApplicationRecordChangesTable,
  ApplicationRecordChangesTableColumn,
} from './ApplicationRecordChangesTable';

const PEOPLE_AND_ENTITIES_COLUMNS: ApplicationRecordChangesTableColumn[] = [
  { headerName: 'Name' },
  { headerName: 'Type' },
  { headerName: 'Details' },
];

const ASSET_COLUMNS: ApplicationRecordChangesTableColumn[] = [
  { headerName: 'Name', minWidth: 128 },
  { headerName: 'Owner', minWidth: 96 },
  { headerName: 'Asset class', minWidth: 96 },
  { headerName: 'Value', align: 'right', minWidth: 128 },
];

const LIFE_INSURANCE_COLUMNS: ApplicationRecordChangesTableColumn[] = [
  { headerName: 'Carrier / type' },
  { headerName: 'Owner' },
  { headerName: 'Insured' },
  { headerName: 'Death benefit', align: 'right' },
  { headerName: 'Details' },
];

const LIABILITY_COLUMNS: ApplicationRecordChangesTableColumn[] = [
  { headerName: 'Name' },
  { headerName: 'Borrower' },
  { headerName: 'Lender' },
  { headerName: 'Amount', align: 'right' },
  { headerName: 'As of' },
];

const PROVISION_COLUMNS: ApplicationRecordChangesTableColumn[] = [
  { headerName: 'From' },
  { headerName: 'Recipient' },
  { headerName: 'Share', align: 'right' },
  { headerName: 'Transfer type' },
];

function getPeopleAndEntitiesRows(
  changes: ApplicationRecordChanges
): string[][] {
  const people = changes.people.map((person) => [
    person.name,
    person.isPrimary ? 'Primary client' : 'Person',
    person.relationships.length
      ? person.relationships
          .map(
            (item) =>
              `${relationshipLabel(item.relationship)}: ${referenceLabel(
                item.person
              )}`
          )
          .join(', ')
      : '—',
  ]);

  const entities = changes.entities.map((entity) => {
    const details = [
      entity.isActivation ? 'Activated' : 'Create entity',
      entity.grantors.length
        ? `Grantors: ${referenceListLabel(entity.grantors)}`
        : '',
      entity.accountOwners.length
        ? `Account owners: ${referenceListLabel(entity.accountOwners)}`
        : '',
      entity.donors.length
        ? `Donors: ${referenceListLabel(entity.donors)}`
        : '',
      ...entity.ownershipStakes.map(
        (stake) =>
          `${referenceLabel(stake.owner)}: ${stake.percentage.toString()}%`
      ),
    ];
    return [
      entity.name,
      entityKindLabel(entity.kind),
      details.filter(Boolean).join('; ') || '—',
    ];
  });

  const testamentaryEntities = changes.testamentaryEntities.map((entity) => [
    entity.name,
    testamentaryEntityKindLabel(entity.kind),
    [
      entity.createdFromEntities.length
        ? `Created from: ${referenceListLabel(entity.createdFromEntities)}`
        : '',
      entity.accountOwner
        ? `Account owner: ${referenceLabel(entity.accountOwner)}`
        : '',
    ]
      .filter(Boolean)
      .join('; ') || '—',
  ]);

  const organizations = changes.organizations.map((organization) => [
    organization.name,
    organizationKindLabel(organization.kind),
    '—',
  ]);

  return [...people, ...entities, ...testamentaryEntities, ...organizations];
}

export function PeopleAndEntitiesChangesTable({
  changes,
}: {
  changes: ApplicationRecordChanges;
}) {
  return (
    <ApplicationRecordChangesTable
      title="People and entities"
      columns={PEOPLE_AND_ENTITIES_COLUMNS}
      rows={getPeopleAndEntitiesRows(changes)}
    />
  );
}

export function AssetsChangesTable({
  assets,
}: {
  assets: ApplicationRecordChanges['assets'];
}) {
  const rows = assets.map((asset) => [
    asset.name,
    referenceLabel(asset.owningEntity),
    asset.assetClass,
    asset.valueChanged
      ? `${formatAssetValue(asset.previousValueUSD)} → ${formatAssetValue(asset.valueUSD)}`
      : formatOptionalCurrency(asset.valueUSD),
  ]);

  return (
    <ApplicationRecordChangesTable
      title="Assets"
      columns={ASSET_COLUMNS}
      rows={rows}
    />
  );
}

function formatAssetValue(
  value: ApplicationRecordChanges['assets'][number]['valueUSD']
): string {
  if (value == null) return 'Not set';
  return formatCurrency(value, {
    minimumFractionDigits: value.isInteger() ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function LifeInsuranceChangesTable({
  lifeInsurancePolicies,
}: {
  lifeInsurancePolicies: ApplicationRecordChanges['lifeInsurancePolicies'];
}) {
  const rows = lifeInsurancePolicies.map((policy) => [
    `${policy.carrierName ?? '—'} - ${insurancePolicyKindLabel(policy.kind)}`,
    referenceLabel(policy.owningEntity),
    referenceListLabel(policy.insuredPeople),
    formatOptionalCurrency(policy.deathBenefitUSD),
    [
      policy.cashValueUSD
        ? `Cash value ${formatOptionalCurrency(policy.cashValueUSD)}`
        : '',
      policy.startDate ? `Starts ${policy.startDate}` : '',
      policy.termDurationYears ? `${policy.termDurationYears}-year term` : '',
      policy.survivorship ? 'Survivorship' : '',
    ]
      .filter(Boolean)
      .join('; ') || '—',
  ]);

  return (
    <ApplicationRecordChangesTable
      title="Life insurance"
      columns={LIFE_INSURANCE_COLUMNS}
      rows={rows}
    />
  );
}

export function LiabilitiesChangesTable({
  liabilities,
}: {
  liabilities: ApplicationRecordChanges['liabilities'];
}) {
  const rows = liabilities.map((liability) => [
    liability.name,
    referenceLabel(liability.borrower),
    referenceLabel(liability.lender),
    formatOptionalCurrency(liability.currentAmountUSD),
    liability.amountAsOfDate ?? '—',
  ]);

  return (
    <ApplicationRecordChangesTable
      title="Liabilities"
      columns={LIABILITY_COLUMNS}
      rows={rows}
    />
  );
}

export function DispositionScenarioProvisionsChangesTables({
  groups,
}: {
  groups: ApplicationRecordChanges['dispositionScenarioGroups'];
}) {
  return groups.map((group) => {
    const clientName = group.firstDeathClient.displayName;
    const rows = group.provisions.map((item) => [
      referenceLabel(group.fundedFrom),
      recipientLabel(item.recipient),
      shareLabel(item.share),
      transferLabel(item),
    ]);

    return (
      <ApplicationRecordChangesTable
        key={`${clientName}|${group.deathEvent}`}
        title="Dispositive provisions"
        context={provisionContextLabel(clientName, group.deathEvent)}
        columns={PROVISION_COLUMNS}
        rows={rows.length ? rows : [['—', 'No provisions', '—', '—']]}
      />
    );
  });
}

export function BeneficiaryDesignationProvisionsChangesTables({
  groups,
}: {
  groups: ApplicationRecordChanges['beneficiaryDesignationGroups'];
}) {
  return groups.map((group) => {
    const tier =
      group.tier === BeneficiaryDesignationTier.Primary
        ? 'Primary beneficiaries'
        : 'Contingent beneficiaries';
    const rows = group.provisions.map((item) => [
      referenceLabel(group.account),
      recipientLabel(item.recipient),
      shareLabel(item.share),
      transferLabel(item),
    ]);
    return (
      <ApplicationRecordChangesTable
        key={`${group.account.displayName}|${group.tier}`}
        title="Beneficiary designations"
        context={`${group.account.displayName} - ${tier}`}
        columns={PROVISION_COLUMNS}
        rows={rows.length ? rows : [['—', 'No beneficiaries', '—', '—']]}
      />
    );
  });
}
